import React, { useState } from 'react';
import { Brain, Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import type { ChatThought } from '../ChatComponents';

interface ThoughtsPanelProps {
  thoughts: ChatThought[];
}

/**
 * Gemini thinking chunk timeline viewer.
 *
 * Admin Playground에서 메시지 옆 토글 버튼으로 접근. 사용자에게 직접 노출되지
 * 않으며 prompt 폭주 패턴 사후 분석용이다. chunk별 elapsed_ms로 timeline 표시.
 */
const ThoughtsPanel: React.FC<ThoughtsPanelProps> = ({ thoughts }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!thoughts || thoughts.length === 0) return null;

  const totalChars = thoughts.reduce((sum, t) => sum + t.text.length, 0);
  const lastMs = thoughts[thoughts.length - 1]?.elapsed_ms ?? 0;

  const handleCopy = async () => {
    const formatted = thoughts.map((t) => t.text).join('\n\n');
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(formatted);
      } else {
        // HTTP(nginx :80)에서 navigator.clipboard 미지원 → execCommand fallback
        const textarea = document.createElement('textarea');
        textarea.value = formatted;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 복사 실패 시 silent (운영 영향 없음)
    }
  };

  return (
    <div className="group relative mt-2 border border-slate-200 rounded-lg bg-slate-50/50" data-testid="thoughts-panel">
      <div className="hidden group-hover:flex absolute -top-3 left-2 z-10 items-center gap-1 bg-white border border-slate-200 rounded-lg shadow-sm px-1 py-0.5">
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="thoughts 복사"
          aria-label={copied ? 'thoughts 복사됨' : 'thoughts 복사'}
          data-testid="thoughts-copy"
        >
          {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
        </button>
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        data-testid="thoughts-toggle"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Brain size={14} className="text-indigo-500" />
        <span className="font-medium">thoughts</span>
        <span className="text-slate-400">
          {thoughts.length} chunks · {totalChars.toLocaleString()} chars · {(lastMs / 1000).toFixed(1)}s
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-2 space-y-1.5" data-testid="thoughts-content">
          {thoughts.map((t) => (
            <div key={t.seq} className="text-[11px] text-slate-700 border-l-2 border-indigo-200 pl-2 py-0.5">
              <div className="text-slate-400 font-mono text-[10px] mb-0.5">
                #{t.seq} · {t.elapsed_ms} ms
              </div>
              <div className="whitespace-pre-wrap break-words">{t.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThoughtsPanel;
